import { call, put, select, spawn, takeLeading } from 'redux-saga/effects';

import { getParameterNames, serverErrors, wishlistErrors } from '@elab/core/utils/constants';
import { actions as uiActions } from '@elab/core/store/ui';
import { selectors as userSelectors, types as userTypes } from '@elab/core/store/user';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import { actions as cartActions, selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as configSelectors } from '@elab/core/store/config';
import { selectors as productSelectors } from '@elab/core/store/product';
import { actions as wishlistsActions } from '@elab/core/store/wishlists';
import { actions as shareWishlistsActions } from '@elab/components/connected/forms/ShareWishlistForm/redux';
import logger from '@elab/core/utils/logger';
import analytics from '@elab/core/utils/analytics';
import authCookiesManager from '@elab/core/utils/authCookiesManager';
import { selectors as selectorsDynaForms } from '@elab/components/connected/forms/EmailGiftCardForm/redux';
import * as storage from '@elab/core/utils/storage';

import { addToWishlistLoggedInUserStrategiesCreator } from './strategies/addToWishlist/loggedInUser';
import { addToWishlistGuestStrategies } from './strategies/addToWishlist/guest';
import api from './api';

import { actions, selectors, types } from '.';

export const addToWishlistLoggedInUserStrategies = addToWishlistLoggedInUserStrategiesCreator(
    addToWishlist,
);

export function* loadWishlist() {
    const anonymous = yield call(authCookiesManager.isAnonymous);
    const multipleWishlistsEnabled = yield select(globalSelectors.multipleWishlistsEnabled);

    if (anonymous || multipleWishlistsEnabled) {
        return;
    }
    yield put(actions.setLoading(true));
    const activeFilter = yield select(selectors.getActiveFilterName);

    try {
        const response = yield call(api.loadWishlist, activeFilter);
        yield put(actions.setWishlist(response));
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield put(actions.loadingFinished());
    }
}

export function* createWishlist({ payload }) {
    yield put(actions.setLoading(true));

    try {
        return yield call(api.createWishlist, payload);
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* loadNewWishlist() {
    const newWishlistName = yield select(selectors.getNewWishlistName);
    const isNewListNameValid = yield select(selectors.isNewListNameValid);
    if (newWishlistName && isNewListNameValid) {
        yield call(createWishlist, { payload: newWishlistName });
        yield put(wishlistsActions.loadAllWishlists());
    }
}

export function* updateWishlist({ payload }) {
    const { productCode, quantity, sharedProduct } = payload;
    yield put(actions.setLoading(true));
    const wishlistCode = yield select(selectors.getWishlistCode);

    try {
        if (!sharedProduct) {
            yield call(api.updateWishList, wishlistCode, productCode, quantity);
            yield put(actions.updatePersonalWishlist(productCode, quantity));
        } else {
            yield put(actions.updateSharedWishlist(productCode, quantity));
        }
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* removeFromWishlist({ payload }) {
    const productCode = payload.productCode || payload;
    yield put(actions.setLoading(true));
    const wishlistCode = yield select(selectors.getWishlistCode);

    try {
        yield call(api.removeFromWishlist, wishlistCode, productCode);
        const useLightWishlist = yield select(configSelectors.getUseLightWishlist);
        const isWishlist =
            window.location.pathname.includes('shoppingList') ||
            window.location.pathname.includes('publicWishlist');
        if (payload.isPlpPageType) {
            yield put(actions.loadWishlist());
        } else {
            if (useLightWishlist && !isWishlist) {
                yield put(actions.loadWishlist());
            } else {
                yield put(actions.loadLightWishlist());
            }
        }

        let newWishlistAnalysticsData = payload.analyticsData;
        if (!newWishlistAnalysticsData) {
            newWishlistAnalysticsData = yield select(selectors.getNewWishlistAnalysticsData);
        }
        const quantity = yield select(productSelectors.getQuantity);
        const isLoyalty = yield select(userSelectors.isLoyalty);
        yield call(
            analytics.handleClickRemoveFromWishlist,
            { ...newWishlistAnalysticsData, isLoyalty },
            productCode,
            quantity,
        );
        if (!payload.analyticsData) {
            yield put(actions.setNewWishlistAnalyticsData(null));
        }
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* removePendingFromWishlist() {
    const productCode = yield select(selectors.getPendingProduct);
    yield put(uiActions.closeDialog());
    yield put(actions.removeFromWishlist(productCode));
    yield put(actions.setPendingProduct(null));
}

export function* addToCart({ payload }) {
    const { productCode, quantity, showConfirmationDialog, analyticsData } = payload;
    yield put(actions.setLoading(true));
    const cartId = yield select(cartSelectors.getCartId);
    const wishlistCode = yield select(selectors.getWishlistCode);

    try {
        const { statusCode, quantityAdded } = yield call(
            api.addToCart,
            wishlistCode,
            cartId,
            productCode,
            quantity,
        );
        yield put(
            uiActions.setMinicartPopoverData({
                productCode,
                quantityAdded,
                statusCode,
            }),
        );
        if (showConfirmationDialog) {
            yield put(
                uiActions.openDialog({
                    name: 'product-cart-popup',
                }),
            );
        } else {
            yield put(uiActions.toggleMinicartPopover(true));
        }
        const payload = {
            code: productCode,
            quantity,
            baseProduct: analyticsData.baseproduct,
            ...analyticsData,
        };
        yield spawn(analytics.handleAddToCart, payload);
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield put(cartActions.loadCart());
        yield call(updateWishListData);
    }
}

export function* loadSelectedWishlist({ payload }) {
    yield put(actions.setLoading(true));

    try {
        let encodedToken = '';
        if (payload.sharedWishlist) {
            const url = window.location.search;
            const params = new URLSearchParams(url);
            const token = params.get('token');
            encodedToken = encodeURIComponent(token);
        }
        const response = yield call(api.loadSelectedWishlist, payload.code, encodedToken);
        if (encodedToken) {
            response.sharedWishlistToken = encodedToken;
        }
        if (!response.name) {
            if (response.errors[0].type === wishlistErrors.EXPIRED_SHARED_WISHLIST_ERROR) {
                yield put(actions.setErrorStateForSharedWishlist(true));
            }
        }
        if (payload.sharedWishlist) {
            yield put(actions.setSharedWishlist(response));
        } else {
            yield put(actions.setWishlist(response));
        }
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield put(actions.loadingFinished());
    }
}

export function* getPrepopulatedData(productCode) {
    let result = {};
    const formsData = yield select(selectorsDynaForms.getData);
    Object.entries(formsData).forEach(([key, value]) => {
        result[key] = value.value;
    });

    result.product = productCode;
    return result;
}

export function* savePrepopulatedData(productCode) {
    const isMultiMode = yield select(globalSelectors.multipleWishlistsEnabled);
    const shouldBeSavedPrepopulatedData = yield select(
        configSelectors.saveGuestInputFromEgiftcardPDP,
        isMultiMode,
    );
    if (shouldBeSavedPrepopulatedData) {
        const dataToSave = yield call(getPrepopulatedData, productCode);
        yield call(storage.set, storage.key.prepopulatedData, dataToSave, true);
    }
}

export function* addToWishlistWithAuthCheck({
    payload: { productCode, egiftcard: isEgiftcard, analytics, isPlpPageType = false },
}) {
    const prevPageData = yield select(globalSelectors.getPrevPageData);
    const egiftcard = isEgiftcard || (prevPageData && prevPageData.isEGiftCard);

    const userId = yield select(userSelectors.getUserId);
    const strategies = yield select(configSelectors.getAddToWishlistStrategies);
    yield put(actions.setLoading(true));

    try {
        if (userId) {
            yield call(
                addToWishlistLoggedInUserStrategies[strategies.loggedInUser],
                productCode,
                egiftcard,
                analytics,
            );
            if (egiftcard && prevPageData && prevPageData.url) {
                yield put(
                    globalActions.setPrevPageData({
                        url: '',
                        isEGiftCard: false,
                    }),
                );
                yield call(storage.remove, storage.key.prepopulatedData);
            }
        } else {
            yield call(savePrepopulatedData, productCode);
            yield put(actions.setNewWishlistAnalyticsData(analytics));
            yield call(
                addToWishlistGuestStrategies[strategies.guest],
                productCode,
                egiftcard,
                analytics,
                isPlpPageType,
            );
        }
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* addToSelectedWishlist({ payload }) {
    const miniPdpProductCode = yield select(productSelectors.getProductCode, 'minipdp');
    const pdpProductCode = yield select(productSelectors.getProductCode);
    const productCode = miniPdpProductCode || pdpProductCode || payload.code;

    const newListName = yield select(selectors.getNewWishlistName);
    const isNewListNameValid = yield select(selectors.isNewListNameValid);
    if (newListName !== null && !isNewListNameValid) {
        return;
    }

    try {
        if (newListName) {
            const newListData = yield call(createWishlist, {
                payload: newListName,
            });
            yield put(actions.setWishlist(newListData));
        }

        const wishlistCode = yield select(selectors.getWishlistCode);
        yield put(actions.setLoading(true));

        const addedSuccessfully = yield call(
            addToWishlist,
            wishlistCode,
            productCode,
            payload.egiftcard,
        );
        const isLoyalty = yield select(userSelectors.isLoyalty);

        yield call(
            analytics.handleClickAddToWishlist,
            { ...payload.analytics, isLoyalty },
            productCode,
        );

        const showPopupAfterAddingProduct = yield select(
            configSelectors.showPopupAfterAddingProduct,
        );

        if (showPopupAfterAddingProduct && addedSuccessfully) {
            yield put(uiActions.openDialog({ name: 'notification-popup' }));
        }

        if (newListName) {
            yield put(wishlistsActions.loadAllWishlists());
        }
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* addToWishlist(wishlistCode, productCode, egiftcard, analyticsData) {
    if (egiftcard) {
        try {
            let prepopulatedData = JSON.parse(
                yield call(storage.get, storage.key.prepopulatedData),
            );
            if (!prepopulatedData) {
                prepopulatedData = yield call(getPrepopulatedData, productCode);
            }

            yield call(api.addEgiftCardToWishlist, wishlistCode, prepopulatedData);
        } catch (e) {
            if (e.message === serverErrors.IllegalStateError) {
                yield put(
                    uiActions.openDialog({
                        name: 'notification-error-popup',
                    }),
                );
                yield put(actions.setErrorWhileAddToWishlist(true));
            }
            return false;
        }
    } else {
        yield call(api.addToWishlist, wishlistCode, productCode);
    }
    let newWishlistAnalysticsData = analyticsData;
    if (!newWishlistAnalysticsData) {
        newWishlistAnalysticsData = yield select(selectors.getNewWishlistAnalysticsData);
    }
    const quantity = yield select(productSelectors.getQuantity);
    const isLoyalty = yield select(userSelectors.isLoyalty);
    yield call(analytics.handleChooseModifaceMode, 'PHOTO_MODE');
    yield call(
        analytics.handleClickAddToWishlist,
        { ...newWishlistAnalysticsData, isLoyalty },
        productCode,
        quantity,
    );
    if (!analyticsData) {
        yield put(actions.setNewWishlistAnalyticsData(null));
    }
    return true;
}

export function* filterWishlist({ payload }) {
    const { activeFilter } = payload;
    yield put(actions.setWishlistFilter(activeFilter));
    yield put(actions.loadWishlist());
}

export function* shareWishlist({ payload }) {
    const { email } = payload;
    const wishlistCode = yield select(selectors.getWishlistCode);
    yield put(actions.setLoading(true));

    try {
        yield call(api.shareWishlist, wishlistCode, email);
        yield put(globalActions.toggleFlashMessage('shareWishlist', 'success'));
    } catch (e) {
        yield put(globalActions.toggleFlashMessage('shareWishlist', 'error'));
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* removeWishlist(action) {
    yield put(actions.setLoading(true));
    try {
        yield call(api.removeWishlist, action.payload);
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield put(wishlistsActions.loadAllWishlists());
    }
}

export function* renameWishlist({ payload }) {
    const { wishlistCode, name } = payload;
    yield put(actions.setLoading(true));
    try {
        yield call(api.renameWishlist, wishlistCode, name);
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield put(wishlistsActions.loadAllWishlists());
    }
}

export function* redirectToEGiftCard(wishlistCode) {
    const eGiftCardEntry = yield select(selectors.getEGiftCardEntry);

    if (!eGiftCardEntry || !eGiftCardEntry.product || !eGiftCardEntry.product.url) {
        return;
    }

    const url = `${eGiftCardEntry.product.url}?${getParameterNames.wishlistCodeForAddToCart}=${wishlistCode}`;
    yield put(globalActions.redirect(url));
}

export function* updateWishListData() {
    const multipleWishlistsEnabled = yield select(globalSelectors.multipleWishlistsEnabled);

    const wishlistUpdateAction = multipleWishlistsEnabled
        ? wishlistsActions.loadAllWishlists
        : actions.loadWishlist;

    yield put(wishlistUpdateAction());
}

export function* handleProductsAddedToCartEvent(productsAddedToCart) {
    for (let p of productsAddedToCart) {
        if (
            typeof p.entry === 'object' &&
            p.entry.product &&
            Object.keys(p.entry.product).length > 0
        ) {
            yield call(analytics.handleAddToCart, p.entry.product);
        }
    }
}

export function* addAllProductsToCart() {
    yield put(actions.setLoading(true));
    const wishlistCode = yield select(selectors.getWishlistCode);
    const cartId = yield select(cartSelectors.getCartId);

    try {
        const productsAddedToCart = yield call(api.addAllProductsToCart, wishlistCode, cartId);
        if (
            productsAddedToCart &&
            Array.isArray(productsAddedToCart.cartModifications) &&
            productsAddedToCart.cartModifications.length > 0
        ) {
            yield spawn(handleProductsAddedToCartEvent, productsAddedToCart.cartModifications);
        }
        yield put(cartActions.loadCart());
        yield put(actions.loadWishlist());
        yield call(redirectToEGiftCard, wishlistCode);
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield call(updateWishListData);
    }
}

export function* addAllSharedWishlistProductsToCart() {
    yield put(actions.setLoading(true));
    const sharedWishlistCode = yield select(selectors.getSharedWishlistCode);
    const sharedWishlistToken = yield select(selectors.getSharedWishlistToken);
    const cartId = yield select(cartSelectors.getCartId);

    try {
        const productsAddedToCart = yield call(
            api.addAllProductsToCart,
            sharedWishlistCode,
            cartId,
            sharedWishlistToken,
        );
        if (
            productsAddedToCart &&
            Array.isArray(productsAddedToCart.cartModifications) &&
            productsAddedToCart.cartModifications.length > 0
        ) {
            yield spawn(handleProductsAddedToCartEvent, productsAddedToCart.cartModifications);
        }
        yield put(cartActions.loadCart());
        yield put(actions.loadWishlist());
        yield call(redirectToEGiftCard, sharedWishlistCode);
    } catch (e) {
        yield spawn(logger.error, e);
    } finally {
        yield put(actions.setLoading(false));
        yield call(updateWishListData);
    }
}

export function* openShareWishlistDialog() {
    yield put(shareWishlistsActions.setVerificationState(false));
    yield put(
        uiActions.openDialog({
            name: 'share-wishlist-popup',
        }),
    );
}

export function* loadLightWishlist() {
    const anonymous = yield call(authCookiesManager.isAnonymous);
    const multipleWishlistsEnabled = yield select(globalSelectors.multipleWishlistsEnabled);
    const useLightWishlist = yield select(configSelectors.getUseLightWishlist);
    if (anonymous || multipleWishlistsEnabled) {
        return;
    }
    if (useLightWishlist) {
        const isWishlist = window.location.pathname.includes('shoppingList');
        if (isWishlist) {
            return;
        }
        yield put(actions.setLoading(true));
        const activeFilter = yield select(selectors.getActiveFilterName);
        try {
            let response;
            response = yield call(api.loadNewWishlist, activeFilter);
            yield put(actions.setLightWishlist(response));
        } catch (e) {
            yield spawn(logger.error, e);
        } finally {
            yield put(actions.setLoading(false));
            yield put(actions.loadingFinished());
        }
    } else {
        yield put(actions.loadLightWishlist());
    }
}

export default [
    takeLeading([types.LOAD_WISHLIST, userTypes.SET_PROFILE], loadLightWishlist),
    takeLeading(types.FILTER_WISHLIST, filterWishlist),
    takeLeading(types.ADD_TO_WISHLIST, addToWishlistWithAuthCheck),
    takeLeading(types.ADD_TO_SELECTED_WISHLIST, addToSelectedWishlist),
    takeLeading(types.SHARE_WISHLIST, shareWishlist),
    takeLeading(types.UPDATE_WISHLIST, updateWishlist),
    takeLeading(types.ADD_TO_CART, addToCart),
    takeLeading(types.CREATE_WISHLIST, createWishlist),
    takeLeading(types.REMOVE_FROM_WISHLIST, removeFromWishlist),
    takeLeading(types.LOAD_SELECTED_WISHLIST, loadSelectedWishlist),
    takeLeading(types.LOAD_NEW_WISHLIST, loadNewWishlist),
    takeLeading(types.REMOVE_WISHLIST, removeWishlist),
    takeLeading(types.REMOVE_PENDING_FROM_WISHLIST, removePendingFromWishlist),
    takeLeading(types.RENAME_WISHLIST, renameWishlist),
    takeLeading(types.ADD_ALL_PRODUCTS_TO_CART, addAllProductsToCart),
    takeLeading(types.ADD_ALL_SHARED_WISHLIST_PRODUCTS_TO_CART, addAllSharedWishlistProductsToCart),
    takeLeading(types.OPEN_SHARE_WISHLIST_DIALOG, openShareWishlistDialog),
    takeLeading(types.LOAD_LIGHT_WISHLIST, loadWishlist),
];
